import { BLOCK_ICON } from '@/components/builder/layers-tree';
import { findNode, isContainer, type BlockKind } from '@personalizehq/editor';
import type { BlockNode } from '@personalizehq/schemas/ir';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

// Click-to-insert. Drag-insert is E2 — and when it arrives it resolves its target through
// `resolveDropTarget` (already in `packages/editor`, already fixture-tested), not through this.

/** Where a click on the palette puts the new block. A container takes it as its LAST child (you
 *  clicked "into" the thing you selected); anything else takes it as its NEXT SIBLING (you cannot
 *  nest a block inside a `text`). Nothing selected → the end of the root. */
export function insertionPoint(root: BlockNode, selection: string | null): { parentId: string; index: number } {
  const hit = selection === null ? undefined : findNode(root, selection);
  if (!hit) return { parentId: root.id, index: isContainer(root) ? root.children.length : 0 };
  if (isContainer(hit.node)) return { parentId: hit.node.id, index: hit.node.children.length };
  // A non-container that is somehow the root (a single `text` document) has nowhere to put a
  // sibling; the caller's insert would throw. Refuse by targeting the root itself.
  if (!hit.parent) return { parentId: root.id, index: 0 };
  return { parentId: hit.parent.id, index: hit.index + 1 };
}

// RANKED, NOT RESTRICTED. Twelve blocks presented with equal weight reads as an invitation to put
// a progress bar in a confirmation sheet — but that is a hierarchy problem, not a permissions one.
// A surface-scoped allowlist would invent a law the IR does not have: `progress` in a bottom sheet
// is legal and renderable (a two-step permission sheet with segmented dots is a real design), and
// blocking it leaves the author who wants one with no path at all. So the rare blocks are demoted,
// not removed. `custom` and `repeat` in particular are power tools — a `custom` needs a host-side
// registration to render anything, and `repeat` only expands against the SDK's campaign list.
export const GROUPS: Array<{ title: string; kinds: readonly BlockKind[]; collapsed?: boolean }> = [
  { title: 'Common', kinds: ['stack', 'box', 'text', 'button', 'image', 'spacer'] },
  { title: 'Layout', kinds: ['scroll', 'grid'] },
  { title: 'Advanced', kinds: ['video', 'progress', 'custom', 'repeat'], collapsed: true },
];

interface PaletteProps {
  onInsert: (kind: BlockKind) => void;
}

export function Palette({ onInsert }: PaletteProps): React.JSX.Element {
  return (
    <div className="p-2">
      {GROUPS.map((group) => (
        <PaletteGroup key={group.title} group={group} onInsert={onInsert} />
      ))}
    </div>
  );
}

function PaletteGroup({
  group,
  onInsert,
}: {
  group: (typeof GROUPS)[number];
  onInsert: (kind: BlockKind) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(!group.collapsed);

  return (
    <div className="mb-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1 px-1 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {group.title}
        {open ? null : <span className="ml-auto tabular-nums">{group.kinds.length}</span>}
      </button>

      {open ? (
        <div className="grid grid-cols-3 gap-1">
          {group.kinds.map((kind) => {
            const Icon = BLOCK_ICON[kind];
            return (
              <button
                key={kind}
                type="button"
                onClick={() => onInsert(kind)}
                className="flex flex-col items-center gap-1 rounded-md border border-transparent px-1 py-2 text-[11px] text-muted-foreground transition-colors hover:border-border hover:bg-secondary/60 hover:text-foreground"
              >
                <Icon className="size-4" />
                {kind}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
